import { prisma, type Prisma } from "client";
import { enrichSessionsWithSpeakers } from "../session-v2";
import { getDayUtcRange } from "../../utils/dayRange";

export const toggleProfileAgendaSession = async ({
  profileId,
  sessionId,
}: {
  profileId: string;
  sessionId: string;
}) => {
  const [session, profile] = await Promise.all([
    prisma.sessionV2.findFirst({
      where: { id: sessionId },
    }),
    prisma.profile.findUnique({
      where: { id: profileId },
      select: { eventId: true },
    }),
  ]);

  if (!session) {
    throw new Error("Session not found");
  }

  if (!profile) {
    throw new Error("Profile not found");
  }

  if (session.eventId !== profile.eventId) {
    throw new Error("Session does not belong to this event");
  }

  const existing = await prisma.profileAgendaSession.findUnique({
    where: {
      profileId_sessionId: { profileId, sessionId },
    },
  });

  if (existing) {
    await prisma.profileAgendaSession.delete({
      where: { id: existing.id },
    });
    return { status: "removed" as const };
  }

  await prisma.profileAgendaSession.create({
    data: { profileId, sessionId },
  });
  return { status: "added" as const };
};

export const getProfileAgendaSessions = async ({
  profileId,
  lang = "en",
  day,
  timezone,
  page = 1,
  limit = 50,
}: {
  profileId: string;
  lang?: string;
  /** Calendar day `YYYY-MM-DD` in event timezone. */
  day?: string;
  timezone?: string;
  page?: number;
  limit?: number;
}) => {
  const where: Prisma.ProfileAgendaSessionWhereInput = {
    profileId,
  };

  if (day && timezone) {
    const { start, end } = getDayUtcRange(day, timezone);
    where.session = {
      startDate: { gte: start, lte: end },
    };
  }

  const [entries, total] = await Promise.all([
    prisma.profileAgendaSession.findMany({
      where,
      include: {
        session: {
          include: {
            translations: lang
              ? { where: { language: { code: lang } } }
              : true,
          },
        },
      },
      skip: (page - 1) * limit,
      take: limit,
      orderBy: { session: { startDate: "asc" } },
    }),
    prisma.profileAgendaSession.count({ where }),
  ]);

  const sessions = entries.map((entry) => entry.session);
  const enriched = await enrichSessionsWithSpeakers(sessions);

  return {
    data: enriched,
    pagination: {
      total,
      currentPage: page,
      totalPages: Math.ceil(total / limit) || 1,
      hasNextPage: page < Math.ceil(total / limit),
      hasPreviousPage: page > 1,
    },
  };
};

export const getProfileAgendaSessionIds = async (
  profileId: string,
  sessionIds: string[]
) => {
  if (!sessionIds.length) return new Set<string>();

  const entries = await prisma.profileAgendaSession.findMany({
    where: {
      profileId,
      sessionId: { in: sessionIds },
    },
    select: { sessionId: true },
  });

  return new Set(entries.map((entry) => entry.sessionId));
};
